import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { GlobalConfiguration } from "../../../../../utils/configurationProvider";
import { toast } from "react-toastify";
import CommonToast from "../../../../../components/_utils/CommonToast";
import ICONS from "../../../../../assets/Icons";
import UniversalRefreshToast from "../../../../../components/_utils/UniversalRefreshToast";
import {
  filedForceApproval,
  filedForceDeniedAccount,
  getSpecificFieldForceAccount,
} from "../../../../../redux/features/accounts/fieldForceAccount";
import { setHideBreadCrumb } from "../../../../../redux/features/hideBreadCrumb/hideBreadCrumb";
import SmallPopupWithInput from "../../../../../components/_utils/CommonSmallPopupWithInput";
import globalText from "../../../../../utils/globalText";
import StatusContainer from "../../../../../components/_utils/CommonStatus/StatusContainer";
import TabsWeapperSection from "../../../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import InfoCard from "../../../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";

const normalizeRecipients = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value) return [];
  return value
    .split(",")
    .map((recipient) => recipient.trim())
    .filter(Boolean);
};

const FieldForceAccountEditRecipientsSuccess = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [rejectPopup, setRejectPopup] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [actionLoading, setActionLoading] = useState("");
  const isActionLoading = actionLoading !== "";
  const [isFocused, setIsFocused] = useState(false);
  const services = useSelector((state) => state.refreshToken.services);
  const { getSpecificFieldForceAccountData } = useSelector(
    (state) => state.fieldForceAccount,
  );
  const fetchedAccount =
    getSpecificFieldForceAccountData?.field_force_accounts?.[
      GlobalConfiguration.lengthCheckZero
    ];
  const account =
    String(fetchedAccount?.id) === String(id) ? fetchedAccount : null;
  const recipients = normalizeRecipients(
    location.state?.recipients ?? account?.recipient_email,
  );

  const viewDetails = () =>
    navigate(
      `/field_force_account_dashboard/field_force_account_view_all/field_force_account_detail_page/${id}`,
      { replace: true },
    );
  const goToDashboard = () =>
    navigate("/field_force_account_dashboard", { replace: true });

  const showActionError = (response, fallbackText) => {
    toast.dismiss();
    CommonToast({
      text:
        response?.payload?.errors?.field_force_account?.[
          GlobalConfiguration.lengthCheckZero
        ] ||
        response?.payload?.errors?.base?.[
          GlobalConfiguration.lengthCheckZero
        ] ||
        fallbackText,
      variant: "var(--error)",
      icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
      buttonNeeded: false,
    });
  };

  const accountApproval = async () => {
    if (isActionLoading) return;
    setActionLoading("approve");
    toast.dismiss();
    UniversalRefreshToast("Approving account");
    try {
      const response = await dispatch(filedForceApproval(id));
      if (response?.payload?.status) {
        toast.dismiss();
        CommonToast({
          text: "Account approved successfully.",
          variant: "var(--success)",
          icon: ICONS.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
          buttonNeeded: false,
        });
        viewDetails();
      } else {
        showActionError(response, "An error occurred while approving account.");
      }
    } catch {
      showActionError(null, "An error occurred while approving account.");
    } finally {
      setActionLoading("");
    }
  };

  const accountRejected = async (reason) => {
    if (isActionLoading) return;
    setActionLoading("reject");
    setRejectPopup(false);
    toast.dismiss();
    UniversalRefreshToast("Rejecting account");
    try {
      const response = await dispatch(
        filedForceDeniedAccount({ id, AccountIdData: reason }),
      );
      if (response?.payload?.status) {
        toast.dismiss();
        CommonToast({
          text: "Account rejected successfully.",
          variant: "var(--success)",
          icon: ICONS.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
          buttonNeeded: false,
        });
        viewDetails();
      } else {
        showActionError(response, "An error occurred while rejecting account.");
      }
    } catch {
      showActionError(null, "An error occurred while rejecting account.");
    } finally {
      setActionLoading("");
    }
  };

  const computeVariant = (value, focused) => {
    const length = (value ?? "").trim().length;
    if (length === 0) return "default";
    if (length >= GlobalConfiguration.rejectReasonMinLength) return "verified";
    return focused ? "typing" : "error";
  };

  const handleInputChange = (event) => {
    const sanitizedValue = event.target.value
      .replace(/\s+/g, " ")
      .replace(/^\s/, "")
      .slice(0, 150);
    setDeclineReason(sanitizedValue);
  };

  useEffect(() => {
    dispatch(setHideBreadCrumb({ hide: true }));
    dispatch(getSpecificFieldForceAccount({ id }));
    return () => dispatch(setHideBreadCrumb({ hide: false }));
  }, [dispatch, id]);

  const buttonRows = [
    ...(services?.includes("FFAAccount") ||
    (services?.includes("Admin") && account?.approval_status === "pending")
      ? [
          [
            {
              btnText: "Reject account",
              enabledBgColor: "var(--negative-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS?.REJECT_ICON_RED_WITH_WHITE_CIRCLE,
              handleFunction: () => {
                if (isActionLoading) return;
                setRejectPopup(true);
              },
              isDisabled: actionLoading === "approve",
              isLoaderNeeded: true,
              isLoading: actionLoading === "reject",
              iconToBeAlign: "left",
              fullWidthRequired: true,
            },
            {
              btnText: "Approve account",
              enabledBgColor: "var(--positive-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
              handleFunction: () => {
                if (isActionLoading) return;
                accountApproval();
              },
              isDisabled: actionLoading === "reject",
              isLoaderNeeded: true,
              isLoading: actionLoading === "approve",
              fullWidthRequired: true,
            },
          ],
        ]
      : []),
    [
      {
        btnText: "View details",
        enabledBgColor: "var(--text-primary)",
        enabledTextColor: "var(--positive-bg)",
        icon: ICONS?.VIEW_DETAILS,
        handleFunction: () => {
          if (isActionLoading) return;
          viewDetails();
        },
        isDisabled: isActionLoading,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        imgColor: "var(--positive-bg)",
      },
    ],
    [
      {
        btnText: "Go to dashboard",
        enabledBgColor: "var(--positive-bg)",
        enabledTextColor: "var(--text-primary)",
        icon: ICONS?.DASHBOARD,
        handleFunction: () => {
          if (isActionLoading) return;
          goToDashboard();
        },
        isDisabled: isActionLoading,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        outLineColor: "var(--text-primary)",
        hoverOption: "black",
      },
    ],
  ];

  return (
    <>
      {rejectPopup && (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-max-zindex bg-custom-backdrop bg-opacity-dark-opacity">
          <SmallPopupWithInput
            headerTitle="Reject account"
            subtext="Provide the reason for rejection."
            headerFontColor="text-primary"
            headerFontWeight="semibold"
            textSize="font-md"
            inputBgColor="var(--primary-bg)"
            inputFgColor="var(--text-secondary)"
            inputOnInput={(e) => {
              handleInputChange(e);
            }}
            noteText={globalText?.REJECT_POPUP_ACCOUNT_NOTE_TEXT}
            noteColor="text-secondary"
            bulletPoints={[
              "New requests and transactions cannot be created for this account.",
              "Before rejecting this account, reject all associated transactions and requests.",
              "Users can edit the rejected account to update details.",
            ]}
            inputValue={declineReason}
            inputPlaceholder={"Reason"}
            inputOnFocus={() => setIsFocused(true)}
            inputOnBlur={() => setIsFocused(false)}
            inputVariant={computeVariant(declineReason, isFocused)}
            inputFontKind="medium"
            inputIsFullWidth={true}
            btnOneHandleFunction={() => {
              setRejectPopup(false);
              setDeclineReason("");
            }}
            btnOneText="Cancel"
            btnOneBtnType="button"
            btnOneEnabledBgColor="trans"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={
              !declineReason ||
              declineReason.length < GlobalConfiguration.panLength
            }
            btnOneIsDisabled={isActionLoading}
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoHoverOption="black"
            btnTwoText="Reject"
            btnTwoHandleFunction={() => {
              accountRejected(declineReason);
              setDeclineReason("");
            }}
            btnTwoBtnType="submit"
            btnTwoInActiveVariantNeeded={true}
          />
        </div>
      )}
      <StatusContainer
        status="success"
        hoverOption="black"
        buttonRows={buttonRows}
        title="Account details updated successfully."
      >
        <TabsWeapperSection title="Updated details">
          <InfoCard
            bgColor="var(--positive-card)"
            rows={[
              {
                leftTitle:
                  recipients.length > GlobalConfiguration.lengthCheckZero
                    ? recipients.join(", ")
                    : "Not given.",
                leftSubTitle: "Recipients (optional)",
              },
            ]}
          />
        </TabsWeapperSection>
      </StatusContainer>
    </>
  );
};

export default FieldForceAccountEditRecipientsSuccess;
